import isNil from "lodash/isNil";
import { useBookingActivityContext } from "PFApp/booking/parts/providers/booking_activity_context_provider";
import { TemplateKey } from "PFApp/constants/templates";
import { useTemplate } from "PFApp/hooks";
import useBookingModulePermissions from "PFApp/use_booking_module_permissions";
import { LoadingDots } from "PFComponents/loading_dots";
import { Select } from "PFComponents/select";
import { useCurrentAccount } from "PFCore/hooks/queries/account";
import { useActivity } from "PFCore/hooks/queries/activity";
import { useCurrentProfile } from "PFCore/hooks/queries/profile";
import { useDateFormatter } from "PFCore/hooks/use_date_formatter";
import { ActivityOption, Template } from "PFTypes";
import { Option } from "PFTypes/options";
import { Controller, useFormContext, useWatch } from "react-hook-form";
import { useTranslation } from "react-i18next";

import { BookingDataItemLimitation, BookingFormMode } from "../booking_form.types";
import { isEditMode } from "../booking_form.utils";
import { BookingFormValues } from "../booking_form_values.types";
import { BookingType } from "../booking_type";
import { EngagementPlaceholder } from "./engagement_placeholder";
import { useEngagementDurationSync } from "./use_engagement_duration_sync";
import { useEngagementOptionsFetcher } from "./use_engagement_options_fetcher";
import { useEngagementSelectionState } from "./use_engagement_selection_state";
import { useEngagementTitleAndCategorySync } from "./use_engagement_title_and_category_sync";

const ENGAGEMENT_SELECTOR_MIN_HEIGHT = 71; // the height of the selector when an option is selected (to prevent flickering the layout)

type EngagementSelectorProps = {
  mode: BookingFormMode;
  hasInitialBookingDates?: boolean;
};

export const EngagementSelector = ({ mode, hasInitialBookingDates = false }: EngagementSelectorProps) => {
  const { t } = useTranslation(["translation", "activities", "core", "bookingModule"]);
  const {
    data: {
      config: { minimum_search_term_length: minimumSearchTermLength }
    }
  } = useCurrentAccount();
  const {
    data: { id: currentProfileId }
  } = useCurrentProfile();
  const { activity, isLoading } = useBookingActivityContext();
  const engagementTemplate = useTemplate(TemplateKey.Engagement as Template["key"]);
  const { formatISODate } = useDateFormatter();
  const { canManagePublicActivityBookings } = useBookingModulePermissions();

  const isInitialActivityEngagement = activity && engagementTemplate?.id === activity.template_id;
  const { control, setValue } = useFormContext<BookingFormValues>();
  const [globalTitle, bookingType, globalCategory, workforceMember, bookings, activityId] = useWatch<
    BookingFormValues,
    ["globalTitle", "bookingType", "globalCategory", "workforceMember", "bookings", "activityId"]
  >({
    name: ["globalTitle", "bookingType", "globalCategory", "workforceMember", "bookings", "activityId"]
  });
  const isRepeated = bookingType === BookingType.Repeated;

  const { data: selectedActivity, isFetching: isFetchingActivity } = useActivity(activityId!, {
    enabled: !!activityId,
    staleTime: Infinity
  });

  const { handleEngagementChange } = useEngagementDurationSync({
    activity,
    selectedActivity,
    isInitialActivityEngagement,
    hasInitialBookingDates,
    mode,
    activityId,
    bookings,
    setValue,
    formatISODate
  });

  const { selectedEngagement, initialEngagementId, handleChange } = useEngagementSelectionState({
    activity,
    isInitialActivityEngagement,
    currentProfileId,
    workforceMember,
    setValue,
    onEngagementChange: handleEngagementChange
  });

  useEngagementTitleAndCategorySync({
    mode,
    selectedActivity,
    isFetchingActivity,
    globalTitle,
    globalCategory,
    bookings,
    setValue
  });

  const minTermLength = minimumSearchTermLength.activities ?? 3;
  const { fetchOptions, ownerId, contributorId } = useEngagementOptionsFetcher({
    workforceMember,
    currentProfileId,
    canManagePublicActivityBookings,
    engagementTemplateKey: engagementTemplate?.key,
    minTermLength
  });

  if (isLoading) {
    return <LoadingDots />;
  }

  if (isRepeated) {
    return <EngagementPlaceholder />;
  }

  const getNoOptionsText = (searchTerm: string) => {
    if (!searchTerm || (minTermLength > 1 && searchTerm.length < minTermLength)) {
      return t("translation:typeNCharactersToGetSuggestions", { count: minTermLength });
    } else {
      return t("core:components.select.noItemsToShow");
    }
  };

  const letClear = !isInitialActivityEngagement || !isEditMode(mode);
  const selectedEngagementId = selectedEngagement?.id ? Number(selectedEngagement.id) : undefined;
  const bookingsToBeAffected =
    bookings?.filter((booking) => booking.limitation === BookingDataItemLimitation.None) ?? [];
  const isEngagementChangedWarningVisible =
    !!selectedEngagement?.id &&
    selectedEngagement.id !== initialEngagementId.current &&
    bookingsToBeAffected.length > 1;

  return (
    <div style={{ minHeight: selectedEngagement ? ENGAGEMENT_SELECTOR_MIN_HEIGHT : undefined }}>
      <Controller
        name="activityId"
        control={control}
        render={({ field }) => (
          <Select<ActivityOption>
            label={t(`activities:templates.engagement`)}
            dropdownId={`engagement-selector-coowned-${ownerId}-${contributorId}`}
            renderDisplayValue={(value) => value?.displayElement}
            fetchOptions={fetchOptions}
            value={selectedEngagementId}
            onChange={(option: Option<ActivityOption>) => handleChange(option.original, field.onChange)}
            onClear={
              letClear && !isNil(selectedEngagementId) ? () => handleChange(null, field.onChange) : undefined
            }
            noOptionsText={getNoOptionsText}
            warning={
              isEngagementChangedWarningVisible
                ? t("bookingModule:bookings.update.engagementWillBeAppliedToBookingsWarning", {
                    count: bookingsToBeAffected.length
                  })
                : undefined
            }
          />
        )}
      />
    </div>
  );
};
